import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Home, ChevronRight } from 'lucide-react'
import { cn } from '../lib/utils'
import { useStandalone } from '../hooks/useStandalone'
import { useIsMobile } from '../hooks/useIsMobile'
import { pathToCrumbs } from '../lib/breadcrumb'

// iOS standalone PWA breadcrumb. Lives in the top-left of the reserved
// safe-area row alongside the existing ProfileDropdown trigger.
// - Gated to standalone + mobile so desktop and browser-tab visits never
//   see it (the back button covers them).
// - Home icon at the start is the fast-home shortcut.
// - Default behavior: derive crumbs from the current pathname. Hides at
//   root paths where the trail would collapse to just "Home".
// - `manualCrumbs` prop: pass an explicit crumb array for state-driven
//   takeovers that aren't URL-routable (PhasesPage, OnboardingWizard
//   editing mode). Each crumb is { key: 'breadcrumb.xxx', to?: '/path' }.
export default function MobileBreadcrumb({ manualCrumbs }) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const isStandalone = useStandalone()
  const isMobile = useIsMobile()

  // PhoenixTracker broadcasts its active tab via this event so we can
  // render a crumb for it — all six tabs share the /phoenix URL, so a
  // pure path-based derivation can't tell them apart.
  const [phoenixTab, setPhoenixTab] = useState(null)
  useEffect(() => {
    const onTab = (e) => setPhoenixTab(e.detail || null)
    window.addEventListener('phoenix:tab', onTab)
    return () => window.removeEventListener('phoenix:tab', onTab)
  }, [])

  if (!isStandalone || !isMobile) return null

  let crumbs
  if (manualCrumbs && manualCrumbs.length > 0) {
    crumbs = manualCrumbs
  } else {
    crumbs = pathToCrumbs(location.pathname, location.search)
    // /phoenix tabs surface the current tab as a single crumb so the
    // user always knows which bottom-nav surface they're on. The Home
    // icon still leads back to the default (Weight) tab.
    if (crumbs.length === 0
        && (location.pathname === '/' || location.pathname === '/')
        && phoenixTab) {
      crumbs = [{ key: `breadcrumb.tab.${phoenixTab}`, to: null }]
    }
  }
  if (crumbs.length === 0) return null

  const goHome = () => {
    navigate('/')
    // State-driven takeovers (Settings, Phases, Onboarding edit) keep the
    // URL at /phoenix while overlaying full-screen pages, so a plain
    // navigate() to /phoenix from inside one of them is a no-op. Emit an
    // app-level "go home" event for those screens to listen for and
    // close themselves.
    window.dispatchEvent(new Event('phoenix:go-home'))
  }

  // Render through a portal so the pill is always anchored to the
  // viewport. Mounting inside a takeover (PhasesPage, OnboardingWizard)
  // would otherwise inherit a transformed ancestor's containing block —
  // the same gotcha CSS `transform` creates for `position: fixed`.
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      className={cn(
        'fixed start-3 z-40 h-10 flex items-center px-1.5 gap-0.5 rounded-full',
        'bg-card/85 backdrop-blur-xl saturate-150 border border-border shadow-sm',
        'text-foreground text-xs overflow-hidden',
        // Cap width so the trail truncates instead of crashing into the
        // ProfileDropdown trigger on the right. Trigger sits at end-3
        // (12px) with 40px width = ~64px reserved.
        'max-w-[calc(100vw-80px)]'
      )}
      style={{ top: 'max(0.75rem, env(safe-area-inset-top))' }}
      role="navigation"
      aria-label={t('breadcrumb.label')}
    >
      <button
        type="button"
        onClick={goHome}
        aria-label={t('breadcrumb.home')}
        className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-accent text-app-primary-500 flex-shrink-0"
      >
        <Home className="w-3.5 h-3.5" />
      </button>

      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1
        return (
          <React.Fragment key={`${c.key}-${i}`}>
            <ChevronRight className="w-3 h-3 text-muted-foreground rtl:-scale-x-100 flex-shrink-0" />
            {c.to && !isLast ? (
              <button
                type="button"
                onClick={() => navigate(c.to)}
                className="px-1.5 truncate text-muted-foreground hover:text-foreground"
              >
                {t(c.key)}
              </button>
            ) : (
              <span className={cn('px-1.5 truncate', isLast ? 'font-medium text-foreground' : 'text-muted-foreground')}>
                {t(c.key)}
              </span>
            )}
          </React.Fragment>
        )
      })}
    </div>,
    document.body
  )
}
